package com.iregained.site.components.widgets

import androidx.compose.runtime.Composable
import com.iregained.site.components.widgets.styles.SharedHeroImageStyle
import com.iregained.site.theme.HeadlineTextStyle
import com.iregained.site.theme.SubheadlineTextStyle
import com.iregained.site.theme.toSitePalette
import com.iregained.site.utils.LocalBreakpoint
import com.iregained.site.utils.Res
import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.animation.Keyframes
import com.varabyte.kobweb.silk.style.animation.toAnimation
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div

// UNIQUE ANIMATIONS: Cinematic Split Entry
val HeroTextSlideRightKeyframes = Keyframes {
    0.percent { Modifier.opacity(0).translateX((-40).px) }
    100.percent { Modifier.opacity(1).translateX(0.px) }
}

val HeroMediaScaleUpKeyframes = Keyframes {
    0.percent { Modifier.opacity(0).scale(0.95f) }
    100.percent { Modifier.opacity(1).scale(1f) }
}

@Composable
fun SharedHeroWidget(
    overline: String,
    headline: String,
    description: String,
    imagePath: String,
    imageAlt: String = "",
    heroYouTubeEmbedUrl: String? = null,
    heroVideoThumbnailUrl: String? = null,
    heroVideoLabelPillText: String? = "Watch",
    heroVideoAriaLabel: String = "Play hero video",
    heroVideoFormat: VideoFormat = VideoFormat.STANDARD_4_3,
    actions: @Composable () -> Unit,
) {
    val breakpoint = LocalBreakpoint.current
    val isDesktop = breakpoint >= Res.Breakpoints.LG

    if (headline.isNotBlank()) {
        Box(modifier = Modifier.fillMaxWidth().styleModifier { property("min-height", "calc(100dvh - 80px)") }
            .padding(leftRight = 5.percent), contentAlignment = Alignment.Center) {
            if (isDesktop) {
                Row(
                    modifier = Modifier.fillMaxWidth().maxWidth(1440.px).gap(100.px).padding(topBottom = 50.px),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    val rigidHalfWidthModifier = Modifier.styleModifier { property("width", "calc(50% - 50px)") }

                    SharedHeroText(
                        overline, headline, description, actions, modifier = rigidHalfWidthModifier.animation(
                            HeroTextSlideRightKeyframes.toAnimation(
                                duration = 1.s, timingFunction = AnimationTimingFunction.EaseOut
                            )
                        )
                    )
                    SharedHeroMedia(
                        imagePath,
                        imageAlt,
                        heroYouTubeEmbedUrl,
                        heroVideoThumbnailUrl,
                        heroVideoLabelPillText,
                        heroVideoAriaLabel,
                        heroVideoFormat,
                        modifier = rigidHalfWidthModifier.animation(
                            HeroMediaScaleUpKeyframes.toAnimation(
                                duration = 1.s, timingFunction = AnimationTimingFunction.EaseOut, delay = 200.ms
                            )
                        )
                    )
                }
            } else {
                Column(
                    modifier = Modifier.fillMaxWidth().padding(topBottom = 25.px),
                    horizontalAlignment = Alignment.CenterHorizontally
                ) {
                    SharedHeroText(
                        overline,
                        headline,
                        description,
                        actions,
                        Modifier.fillMaxWidth().margin(bottom = 40.px).animation(
                            HeroTextSlideRightKeyframes.toAnimation(
                                duration = 1.s, timingFunction = AnimationTimingFunction.EaseOut
                            )
                        ),
                        isMobile = true
                    )
                    SharedHeroMedia(
                        imagePath,
                        imageAlt,
                        heroYouTubeEmbedUrl,
                        heroVideoThumbnailUrl,
                        heroVideoLabelPillText,
                        heroVideoAriaLabel,
                        heroVideoFormat,
                        modifier = Modifier.fillMaxWidth().animation(
                            HeroMediaScaleUpKeyframes.toAnimation(
                                duration = 1.s, timingFunction = AnimationTimingFunction.EaseOut, delay = 200.ms
                            )
                        )
                    )
                }
            }
        }
    }
}

@Composable
private fun SharedHeroText(
    overline: String,
    headline: String,
    description: String,
    actions: @Composable () -> Unit,
    modifier: Modifier,
    isMobile: Boolean = false
) {
    val palette = ColorMode.current.toSitePalette()
    val align = if (isMobile) Alignment.CenterHorizontally else Alignment.Start

    Column(modifier = modifier, horizontalAlignment = align) {
        if (overline.isNotBlank()) SpanText(
            text = overline,
            modifier = Modifier.fontSize(if (isMobile) 24.px else 32.px).fontWeight(FontWeight.Bold)
                .color(palette.custom.darkText).opacity(70.percent).lineHeight(1.2).margin(bottom = 8.px)
        )

        // THE FIX: Upgraded to a Div with innerHTML injection to support tags like <sup>, <b>, <br>
        if (headline.isNotBlank()) {
            Div(
                attrs = HeadlineTextStyle.toModifier().color(palette.brand.primary).fontWeight(FontWeight.ExtraBold)
                    .margin(bottom = 16.px).toAttrs {
                        ref { htmlElement -> htmlElement.innerHTML = headline; onDispose {} }
                    }
            )
        }

        if (description.isNotBlank()) Div(
            attrs = SubheadlineTextStyle.toModifier().color(palette.custom.darkText).fontWeight(FontWeight.Medium)
                .margin(bottom = 40.px)
                .toAttrs { ref { htmlElement -> htmlElement.innerHTML = description; onDispose {} } })

        Row(
            modifier = Modifier.gap(20.px).flexWrap(FlexWrap.Wrap), verticalAlignment = Alignment.CenterVertically
        ) { actions() }
    }
}

@Composable
private fun SharedHeroMedia(
    imagePath: String,
    imageAlt: String,
    youtubeEmbedUrl: String?,
    videoThumbnailUrl: String?,
    videoLabelPillText: String?,
    videoAriaLabel: String,
    videoFormat: VideoFormat,
    modifier: Modifier
) {
    val breakpoint = LocalBreakpoint.current
    val isMobile = breakpoint < Res.Breakpoints.MD
    val containerRatio = when (videoFormat) {
        VideoFormat.CINEMATIC_ULTRAWIDE -> if (isMobile) "16/9" else "1920/600"; VideoFormat.SQUARE_1_1 -> if (isMobile) "4/3" else "1/1"; VideoFormat.STANDARD_4_3 -> "4/3"; VideoFormat.STANDARD_16_9 -> "16/9"
    }

    Box(modifier = modifier, contentAlignment = Alignment.Center) {
        Box(modifier = Modifier.fillMaxWidth().styleModifier { property("aspect-ratio", containerRatio) }
            .borderRadius(16.px).overflow(Overflow.Hidden)
            .then(if (youtubeEmbedUrl.isNullOrBlank()) SharedHeroImageStyle.toModifier() else Modifier)) {
            if (!youtubeEmbedUrl.isNullOrBlank()) {
                YouTubeVideoCard(
                    embedUrl = youtubeEmbedUrl,
                    thumbnailUrl = videoThumbnailUrl,
                    ariaLabel = videoAriaLabel,
                    labelPillText = videoLabelPillText,
                    videoFormat = videoFormat,
                    modifier = Modifier.fillMaxSize()
                )
            } else if (imagePath.isNotBlank()) {
                Image(src = imagePath, alt = imageAlt, modifier = Modifier.fillMaxSize().objectFit(ObjectFit.Cover))
            }
        }
    }
}

@Composable
fun SharedHeroActionLink(text: String, path: String, styleModifier: Modifier) {
    if (text.isNotBlank() && path.isNotBlank()) {
        Link(path = path, modifier = Modifier.textDecorationLine(TextDecorationLine.None)) {
            Box(
                modifier = styleModifier.minWidth(200.px).height(60.px).padding(leftRight = 24.px).borderRadius(8.px)
                    .cursor(Cursor.Pointer), contentAlignment = Alignment.Center
            ) {
                SpanText(text, modifier = Modifier.fontSize(20.px).fontWeight(FontWeight.SemiBold))
            }
        }
    }
}